import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';

export interface FileNavigatorProps {
  /** Current path being displayed (relative, no leading "/") */
  currentPath: string;

  /** Callback when user navigates to a different path (relative, no leading "/") */
  onNavigate: (path: string) => void;

  /** Base path that users cannot navigate above (relative, no leading "/") */
  basePath: string;

  /** Loading state for navigation operations */
  loading?: boolean;

  /** Error message to display */
  error?: string;
}

type Crumb = { label: string; path: string };

/**
 * Normalizes a relative path by trimming, removing leading/trailing slashes, and replacing backslashes with forward slashes.
 * @param input - The input path to normalize
 * @returns The normalized path
 */
function normalizeRelPath(input: string): string {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return '';
  // Normalize slashes; DO NOT introduce leading "/"
  return trimmed
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/^\/+|\/+$/g, '');
}

/**
 * Determines whether a path is under a base path.
 * @param path - The path to check
 * @param base - The base path to check against
 * @returns Whether the path is under the base path
 */
function isUnderBase(path: string, base: string): boolean {
  if (!base) return true;
  return path === base || path.startsWith(`${base}/`);
}

/**
 * Builds breadcrumbs for a given base path and current path.
 * @param basePath - The base path to use for breadcrumbs
 * @param currentPath - The current path to use for breadcrumbs
 * @returns An array of breadcrumbs, where each breadcrumb is an object containing the label and path
 */
function buildBreadcrumbs(basePath: string, currentPath: string): Crumb[] {
  const base = normalizeRelPath(basePath);
  const current = normalizeRelPath(currentPath);

  const safeCurrent = isUnderBase(current, base) ? current : base;

  const crumbs: Crumb[] = [{ label: 'Files', path: base }];

  if (!safeCurrent || safeCurrent === base) return crumbs;

  // Determine path portion after base (boundary-safe)
  const remainder = !base
    ? safeCurrent
    : safeCurrent === base
      ? ''
      : safeCurrent.startsWith(`${base}/`)
        ? safeCurrent.slice(base.length + 1)
        : '';

  const segments = remainder.split('/').filter(Boolean);

  let acc = base;
  for (const seg of segments) {
    acc = acc ? `${acc}/${seg}` : seg;
    crumbs.push({ label: seg, path: acc });
  }

  return crumbs;
}

/**
 * FileNavigator Component
 *
 * Breadcrumb navigation for the file manager.
 * Emits relative paths only (no leading slash) to match backend validation.
 */
const FileNavigator: React.FC<FileNavigatorProps> = ({
  currentPath,
  onNavigate,
  basePath,
  loading = false,
  error,
}) => {
  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(basePath, currentPath),
    [basePath, currentPath]
  );

  const normalizedBase = normalizeRelPath(basePath);
  const normalizedCurrent = normalizeRelPath(currentPath);

  // Compute parent path for the "up" button
  const isAtRoot = !normalizedCurrent || normalizedCurrent === normalizedBase;
  const parentPath = useMemo(() => {
    if (isAtRoot) return normalizedBase;
    const segments = normalizedCurrent.split('/').filter(Boolean);
    segments.pop();
    return segments.length ? segments.join('/') : normalizedBase;
  }, [normalizedCurrent, normalizedBase, isAtRoot]);

  const handleNavigate = (targetPath: string) => {
    const normalizedTarget = normalizeRelPath(targetPath);

    // Prevent navigation above base path (UI guard only)
    if (!isUnderBase(normalizedTarget, normalizedBase)) return;

    onNavigate(normalizedTarget);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Go to parent folder">
          <span>
            <IconButton
              size="small"
              onClick={() => handleNavigate(parentPath)}
              disabled={isAtRoot}
              aria-label="Navigate to parent folder"
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="file navigation breadcrumb"
          sx={{ flexGrow: 1 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            const key = crumb.path ? crumb.path : `__root__${index}`;

            if (isLast) {
              return (
                <Typography
                  key={key}
                  color="text.primary"
                  sx={{ fontWeight: 'medium' }}
                >
                  {crumb.label}
                </Typography>
              );
            }

            return (
              <Link
                key={key}
                component="button"
                variant="body1"
                onClick={() => handleNavigate(crumb.path)}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>

        {loading && <CircularProgress size={20} />}
      </Box>
    </Box>
  );
};

export default FileNavigator;
